import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  ArrowLeft,
  Save,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import Layout from '../components/Layout';
// eslint-disable-next-line no-unused-vars
import authService from '../services/authService';
import barStockQuoteService from '../services/barStockQuoteService';

function BarStockQuoteFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  // User info available for future use
  // const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  // Form state
  const [formData, setFormData] = useState({
    diameter: '',
    length: '310',
    unit_price_tax_included: '',
    tax_rate: '0.13',
    supplier_name: '',
    quote_date: new Date().toISOString().split('T')[0],
    notes: '',
  });

  // Helper to parse date from various formats (MongoDB $date or plain string)
  const parseQuoteDate = (dateValue) => {
    if (!dateValue) return new Date().toISOString().split('T')[0];
    
    // Handle MongoDB format: {$date: '2026-03-08T09:53:10.606Z'}
    if (typeof dateValue === 'object' && dateValue.$date) {
      return new Date(dateValue.$date).toISOString().split('T')[0];
    }
    
    // Handle plain string
    try {
      return new Date(dateValue).toISOString().split('T')[0];
    } catch {
      return new Date().toISOString().split('T')[0];
    }
  };

  // Load quote data for edit mode
  const loadQuote = useCallback(async () => {
    if (!isEditMode) return;

    try {
      setLoading(true);
      setError(null);

      const response = await barStockQuoteService.getQuote(id);
      // Handle both flat and nested response formats
      const quote = response.data || response;
      console.log('Quote data received:', quote);
      
      setFormData({
        diameter: quote.diameter?.toString() || '',
        length: quote.length?.toString() || '310',
        unit_price_tax_included: quote.unit_price_tax_included?.toString() || '',
        tax_rate: quote.tax_rate?.toString() || '0.13',
        supplier_name: quote.supplier_name || '',
        quote_date: parseQuoteDate(quote.quote_date),
        notes: quote.notes || '',
      });
    } catch (err) {
      console.error('Failed to load quote:', err);
      setError('加载报价数据失败，请重试。');
    } finally {
      setLoading(false);
    }
  }, [id, isEditMode]);

  useEffect(() => {
    loadQuote();
  }, [loadQuote]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear validation error when user types
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.diameter || parseFloat(formData.diameter) <= 0) {
      errors.diameter = '请输入有效的直径';
    }

    if (!formData.unit_price_tax_included || parseFloat(formData.unit_price_tax_included) <= 0) {
      errors.unit_price_tax_included = '请输入有效的含税单价';
    }

    if (!formData.supplier_name.trim()) {
      errors.supplier_name = '请输入供应商名称';
    }

    if (!formData.quote_date) {
      errors.quote_date = '请选择报价日期';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const payload = {
        diameter: parseFloat(formData.diameter),
        length: parseInt(formData.length) || 310,
        unit_price_tax_included: parseFloat(formData.unit_price_tax_included),
        tax_rate: parseFloat(formData.tax_rate) || 0.13,
        supplier_name: formData.supplier_name.trim(),
        quote_date: formData.quote_date,
        notes: formData.notes.trim(),
      };

      if (isEditMode) {
        await barStockQuoteService.updateQuote(id, payload);
      } else {
        await barStockQuoteService.createQuote(payload);
      }

      navigate('/bar-stock-quotes');
    } catch (err) {
      console.error('Failed to save quote:', err);
      setError(err.response?.data?.error || '保存失败，请重试。');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/bar-stock-quotes');
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="outline" size="icon" onClick={handleCancel}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <h1 className="text-2xl font-bold">
            {isEditMode ? '编辑棒料报价' : '添加棒料报价'}
          </h1>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="w-4 h-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Form */}
        <Card>
          <CardHeader>
            <CardTitle>报价信息</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                {/* Diameter */}
                <div className="space-y-2">
                  <Label htmlFor="diameter">
                    直径 (mm) <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="diameter"
                    name="diameter"
                    type="number"
                    step="0.1"
                    value={formData.diameter}
                    onChange={handleInputChange}
                    placeholder="例如: 5.0"
                    className={validationErrors.diameter ? 'border-red-500' : ''}
                  />
                  {validationErrors.diameter && (
                    <p className="text-sm text-red-500">{validationErrors.diameter}</p>
                  )}
                </div>

                {/* Length */}
                <div className="space-y-2">
                  <Label htmlFor="length">
                    长度 (mm)
                  </Label>
                  <Input
                    id="length"
                    name="length"
                    type="number"
                    value={formData.length}
                    onChange={handleInputChange}
                    placeholder="默认: 310"
                  />
                </div>

                {/* Unit Price (Tax Included) */}
                <div className="space-y-2">
                  <Label htmlFor="unit_price_tax_included">
                    含税单价 (¥) <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="unit_price_tax_included"
                    name="unit_price_tax_included"
                    type="number"
                    step="0.01"
                    value={formData.unit_price_tax_included}
                    onChange={handleInputChange}
                    placeholder="例如: 150.23"
                    className={validationErrors.unit_price_tax_included ? 'border-red-500' : ''}
                  />
                  {validationErrors.unit_price_tax_included && (
                    <p className="text-sm text-red-500">{validationErrors.unit_price_tax_included}</p>
                  )}
                </div>

                {/* Tax Rate */}
                <div className="space-y-2">
                  <Label htmlFor="tax_rate">
                    税率
                  </Label>
                  <Input
                    id="tax_rate"
                    name="tax_rate"
                    type="number"
                    step="0.01"
                    value={formData.tax_rate}
                    onChange={handleInputChange}
                    placeholder="默认: 0.13"
                  />
                </div>

                {/* Supplier Name */}
                <div className="space-y-2">
                  <Label htmlFor="supplier_name">
                    供应商名称 <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="supplier_name"
                    name="supplier_name"
                    type="text"
                    value={formData.supplier_name}
                    onChange={handleInputChange}
                    placeholder="例如: 厦门金鹭硬质合金有限公司"
                    className={validationErrors.supplier_name ? 'border-red-500' : ''}
                  />
                  {validationErrors.supplier_name && (
                    <p className="text-sm text-red-500">{validationErrors.supplier_name}</p>
                  )}
                </div>

                {/* Quote Date */}
                <div className="space-y-2">
                  <Label htmlFor="quote_date">
                    报价日期 <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="quote_date"
                    name="quote_date"
                    type="date"
                    value={formData.quote_date}
                    onChange={handleInputChange}
                    className={validationErrors.quote_date ? 'border-red-500' : ''}
                  />
                  {validationErrors.quote_date && (
                    <p className="text-sm text-red-500">{validationErrors.quote_date}</p>
                  )}
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes">备注</Label>
                <Textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="可选：添加备注信息"
                  rows={4}
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-4 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  取消
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      保存中...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      保存
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}

export default BarStockQuoteFormPage;
